<template>
  <div class="font-sans min-h-screen bg-white dark:bg-gray-900">
    <HeroSection
      @search="handleSearch"
      :initial-search="route.query.search || (route.params.kota ? route.params.kota.replace(/-/g, ' ') : '')"
      :initial-location="route.query.location || ''"
      :initial-education="route.query.education || ''"
      :initial-experience="route.query.experience || ''"
      :is-loading="loading"
    />

    <section id="recommended-jobs-section" class="py-3 bg-white dark:bg-gray-900 w-full">
      <div class="max-w-7xl mx-auto px-3 sm:px-4">
        <h2
          class="text-xl sm:text-2xl font-bold sm:font-extrabold text-[#293146] dark:text-white text-center mb-4"
        >
          {{ $t('home.recommendedJobs') }}
        </h2>

        <div class="relative group">
          <div
            data-testid="job-slider"
            :ref="autoScroll.sliderRef"
            id="job-slider"
            class="flex overflow-x-auto gap-3 pb-4 pt-1 snap-x scrollbar-hide px-1"
            @mouseenter="autoScroll.pause"
            @mouseleave="autoScroll.resume"
          >
            <template v-if="recommendedJobs.length > 0">
              <JobCard
                v-for="job in recommendedJobs"
                :key="job.id"
                :job="job"
                data-testid="job-card"
              />
            </template>
            <!-- Shimmer: hanya saat loading awal (belum pernah retry) -->
            <template v-else-if="loadingRecommended && recommendedJobs.length === 0">
              <JobCardShimmer v-for="i in 5" :key="i" />
            </template>
            <!-- ErrorState: tampil saat retry berlangsung ATAU setelah semua retry gagal -->
            <ErrorState
              v-else-if="(recommendedError || isRetryingRecommended) && recommendedJobs.length === 0"
              :message="recommendedError || 'Koneksi lambat, mencoba ulang...'"
              :is-retrying="isRetryingRecommended"
              :retry-count="retryCountRecommended"
              :compact="true"
              @retry="handleRetryRecommended"
              @refresh="handleRefresh"
            />
          </div>

          <button
            data-testid="slider-scroll-left"
            @click="handleSliderNav('left')"
            class="absolute -left-3 sm:-left-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white dark:bg-gray-700 rounded-full shadow-md border border-gray-100 dark:border-gray-600 flex items-center justify-center text-gray-500 dark:text-gray-300 hover:text-[#293146] dark:hover:text-white hover:border-[#C5B47A] dark:hover:border-white transition-colors z-10 hidden md:flex opacity-0 group-hover:opacity-100 focus:outline-none"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              ></path>
            </svg>
          </button>
          <button
            data-testid="slider-scroll-right"
            @click="handleSliderNav('right')"
            class="absolute -right-3 sm:-right-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white dark:bg-gray-700 rounded-full shadow-md border border-gray-100 dark:border-gray-600 flex items-center justify-center text-gray-500 dark:text-gray-300 hover:text-[#293146] dark:hover:text-white hover:border-[#C5B47A] dark:hover:border-white transition-colors z-10 hidden md:flex opacity-0 group-hover:opacity-100 focus:outline-none"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              ></path>
            </svg>
          </button>
        </div>
      </div>
    </section>

    <section
      id="latest-jobs"
      class="py-4 sm:py-6 bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 w-full"
    >
      <div class="max-w-screen-xl mx-auto px-3 sm:px-4">
        <div class="flex flex-col lg:flex-row gap-6">
          <div class="w-full">
            <!-- Changed from lg:w-3/4 to w-full -->
            <div
              class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-2"
            >
              <h2
                class="text-lg sm:text-xl font-bold text-[#293146] dark:text-white flex items-center gap-2"
              >
                <Icon icon="mdi:briefcase-outline" class="w-5 h-5 text-[#C5B47A]" />
                {{ latestJobsTitle }}
              </h2>
              <div class="flex items-center gap-2">
                <span
                  v-if="route.query.search || route.params.kota"
                  data-testid="active-search-tag"
                  class="inline-flex items-center px-2.5 py-1 bg-[#293146] dark:bg-gray-700 text-white dark:text-gray-100 text-xs sm:text-sm font-medium rounded-lg"
                >
                  {{ route.query.search || (route.params.kota ? route.params.kota.replace(/-/g, ' ') : '') }}
                  <button
                    data-testid="clear-search-btn"
                    @click="clearSearch"
                    class="ml-1.5 hover:text-gray-200 dark:hover:text-gray-300"
                  >
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  </button>
                </span>
                <span
                  data-testid="jobs-count"
                  class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-medium"
                >
                  {{
                    isFiltered
                      ? $t('home.jobsFoundFiltered', {
                          count: latestJobs.length,
                          total: lowonganStore.totalJobs,
                          totalOverall: lowonganStore.totalOverallJobs,
                        })
                      : $t('home.jobsFound', {
                          count: latestJobs.length,
                          total: lowonganStore.totalJobs,
                        })
                  }}
                </span>
              </div>
            </div>

            <div data-testid="jobs-list" class="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <!-- Added grid classes here -->
              <!-- Shimmer: hanya saat loading awal (belum pernah retry) -->
              <template v-if="loading && !isRetryingLatest && latestJobs.length === 0">
                <div
                  v-for="i in 6"
                  :key="i"
                  class="bg-white dark:bg-gray-700 rounded-xl border border-gray-100 dark:border-gray-600 p-3 sm:p-4 shadow-sm"
                >
                  <div class="flex flex-col md:flex-row md:items-center gap-3">
                    <div class="flex items-center gap-3 flex-1">
                      <div
                        class="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded-lg shimmer shrink-0"
                      ></div>
                      <div class="flex-1 space-y-1.5">
                        <div
                          class="h-4 w-40 sm:w-48 md:w-56 bg-gray-200 dark:bg-gray-600 rounded shimmer"
                        ></div>
                        <div
                          class="h-3 w-28 sm:w-32 bg-gray-200 dark:bg-gray-600 rounded shimmer"
                        ></div>
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-1.5">
                      <div class="h-5 w-16 bg-gray-200 dark:bg-gray-600 rounded-full shimmer"></div>
                      <div class="h-5 w-20 bg-gray-200 dark:bg-gray-600 rounded-full shimmer"></div>
                    </div>
                  </div>
                  <div
                    class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-600 flex flex-col sm:flex-row sm:items-center justify-between gap-2"
                  >
                    <div class="flex items-center gap-3">
                      <div class="h-3 w-24 bg-gray-200 dark:bg-gray-600 rounded shimmer"></div>
                      <div class="h-3 w-20 bg-gray-200 dark:bg-gray-600 rounded shimmer"></div>
                    </div>
                    <div class="h-8 w-24 bg-gray-200 dark:bg-gray-600 rounded-lg shimmer"></div>
                  </div>
                </div>
              </template>
              <!-- ErrorState: tampil saat retry berlangsung ATAU setelah semua retry gagal -->
              <ErrorState
                v-else-if="(latestError || isRetryingLatest) && latestJobs.length === 0"
                :message="latestError || 'Koneksi lambat, mencoba ulang...'"
                :is-retrying="isRetryingLatest"
                :retry-count="retryCountLatest"
                @retry="handleRetryLatest"
                @refresh="handleRefresh"
              />
              <template v-else-if="latestJobs.length > 0">
                <JobListItem
                  v-for="job in latestJobs"
                  :key="job.id"
                  :job="job"
                  data-testid="job-list-item"
                />
              </template>
              <div
                v-else-if="!loading && !latestError && hasMore === false"
                data-testid="empty-state"
                class="text-center text-gray-400 dark:text-gray-500 py-8 sm:py-10"
              >
                <svg
                  class="w-12 h-12 sm:w-14 sm:h-14 mx-auto text-gray-300 dark:text-gray-600 mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  ></path>
                </svg>
                <p class="text-sm text-gray-400 dark:text-gray-500">
                  {{ $t('home.noJobsAvailable') }}
                </p>
              </div>

              <div
                :ref="infiniteScroll.triggerRef"
                data-testid="load-more-trigger"
                class="text-center py-4"
              >
                <div
                  v-if="loading && latestJobs.length > 0"
                  data-testid="loading-more"
                  class="inline-flex items-center justify-center px-3 py-1.5 text-xs sm:text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900 rounded-full"
                >
                  <svg
                    class="w-3 h-3 mr-1.5 text-blue-600 dark:text-blue-400 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {{ $t('home.loadingMore') }}
                </div>
              </div>
            </div>
          </div>

          <!-- JobFilterSidebar component removed from here -->
        </div>
      </div>
    </section>

    <div
      v-if="toast.show"
      class="fixed bottom-5 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100%-2rem)] max-w-md"
      @click.self="dismissToast"
    >
      <div
        class="flex items-center justify-between p-4 bg-[#1a2b4b] dark:bg-gray-700 text-white dark:text-gray-100 rounded-lg shadow-lg"
      >
        <span class="text-sm sm:text-base font-medium">{{ toast.message }}</span>
        <div class="flex items-center gap-2">
          <button
            @click="viewLatestJobs"
            class="px-3 py-1.5 text-xs sm:text-sm font-semibold bg-white/10 hover:bg-white/20 rounded-md"
          >
            Lihat
          </button>
          <button @click="dismissToast" class="p-1">
            <Icon icon="mdi:close" class="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>

    <div id="app-footer">
      <AppFooter />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, watch } from 'vue';
import { event as gtagEvent } from 'vue-gtag';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Icon } from '@iconify/vue';
import { initFlowbite } from 'flowbite';
import { useLowonganStore } from '../stores/lowongan';
import { useAutoScroll } from '../composables/useAutoScroll';
import { useInfiniteScroll } from '../composables/useInfiniteScroll';
import { useJobFilters } from '../composables/useJobFilters';
import { useSeoMeta, getWebSiteSchema } from '../composables/useSeoMeta';
import AppFooter from '../components/layout/AppFooter.vue';
import HeroSection from '../components/home/HeroSection.vue';
import JobCard from '../components/home/JobCard.vue';
import JobCardShimmer from '../components/home/JobCardShimmer.vue';
import JobListItem from '../components/home/JobListItem.vue';
import ErrorState from '../components/common/ErrorState.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const lowonganStore = useLowonganStore();

const locationParam = computed(() => route.params.kota);

const seoTitle = computed(() => {
  if (locationParam.value) {
    const kota = locationParam.value.replace(/-/g, ' ');
    const capitalizedKota = kota.replace(/\b\w/g, l => l.toUpperCase());
    return `Info Lowongan Kerja ${capitalizedKota} Terbaru 2026 - Fresh Graduate & Tanpa Pengalaman`;
  }
  return 'Platform Lowongan Kerja Terpercaya di Jambi';
});

const seoDescription = computed(() => {
  if (locationParam.value) {
    const kota = locationParam.value.replace(/-/g, ' ');
    const capitalizedKota = kota.replace(/\b\w/g, l => l.toUpperCase());
    return `Temukan ribuan info lowongan kerja ${capitalizedKota} terbaru 2026. Tersedia loker untuk fresh graduate, baru lulus, dan tanpa pengalaman. Lowongan full-time, part-time, dan freelance dari perusahaan terkemuka di ${capitalizedKota}.`;
  }
  return 'Temukan ribuan lowongan kerja terbaru di Jambi. Tersedia lowongan untuk fresh graduate dan tanpa pengalaman. Lowongan full-time, part-time, dan freelance dari perusahaan terkemuka. Cari kerja sesuai pendidikan dan pengalamanmu di Lokerjambi.co.id.';
});

const seoPath = computed(() => locationParam.value ? `/lokasi/${locationParam.value}` : '/');

useSeoMeta({
  title: seoTitle,
  description: seoDescription,
  path: seoPath,
  jsonLd: getWebSiteSchema(),
});
const autoScroll = useAutoScroll();
const infiniteScroll = useInfiniteScroll();
const jobFilters = useJobFilters(router, lowonganStore);

const recommendedJobs = computed(() => lowonganStore.recommendedJobs);
const latestJobs = computed(() => lowonganStore.latestJobs);
const loading = computed(() => lowonganStore.loading);
const loadingRecommended = computed(() => lowonganStore.loadingRecommended);
const hasMore = computed(() => lowonganStore.hasMore);
const isRetryingRecommended = computed(() => lowonganStore.isRetryingRecommended);
const retryCountRecommended = computed(() => lowonganStore.retryCountRecommended);
const isRetryingLatest = computed(() => lowonganStore.isRetryingLatest);
const retryCountLatest = computed(() => lowonganStore.retryCountLatest);
const recommendedError = computed(() => lowonganStore.recommendedError);
const latestError = computed(() => lowonganStore.latestError);

const isFiltered = computed(() => {
  const filterKeys = ['search', 'location', 'education', 'experience', 'job_type', 'gender', 'kategori', 'gaji_min', 'gaji_max', 'age_range'];
  return filterKeys.some(key => !!route.query[key]) || !!route.params.kota;
});

const latestJobsTitle = computed(() => {
  const date = new Date();
  const month = date.toLocaleString('id-ID', { month: 'long' });
  const year = date.getFullYear();
  return `Lowongan Terbaru ${month} ${year}`;
});

const toast = reactive({ show: false, message: '', type: 'success' });
let lastSearchParams = null;

const showToast = (message, type = 'success') => {
  toast.message = message;
  toast.type = type;
  toast.show = true;
};

const dismissToast = () => {
  toast.show = false;
};

const viewLatestJobs = () => {
  const latestJobsSection = document.getElementById('latest-jobs');
  if (latestJobsSection) {
    latestJobsSection.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
  dismissToast();
};

const handleSearch = (searchParams) => {
  lastSearchParams = searchParams;
  if (route.params.kota) {
    // Navigate back to home and apply search if we are on a location route and searching for something else
    router.push({ path: '/', query: searchParams });
  } else {
    jobFilters.setFilters({
      search: searchParams.search,
      location: searchParams.location,
      education: searchParams.education,
      experience: searchParams.experience,
    });
  }
};

const clearSearch = () => {
  if (route.params.kota) {
    router.push('/');
  } else {
    jobFilters.clearSearch();
  }
};

// handleFilter function removed as JobFilterSidebar is removed.

const handleRetryRecommended = async () => {
  await lowonganStore.retryRecommended();
};

const handleRetryLatest = async () => {
  await lowonganStore.retryLatest();
};

const handleRefresh = () => {
  window.location.reload();
};

watch(
  () => route.query,
  (newQuery, oldQuery) => {
    if (JSON.stringify(newQuery) !== JSON.stringify(oldQuery)) {
      const urlFilters = jobFilters.initFromUrl(newQuery);
      lowonganStore.setFilters(urlFilters);
    }
  },
  { deep: true }
);

// Watch route.params.kota for client-side navigation between /lokasi/:kota pages
watch(
  () => route.params.kota,
  (newKota, oldKota) => {
    if (newKota !== oldKota) {
      const search = newKota ? newKota.replace(/-/g, ' ') : '';
      const urlFilters = jobFilters.initFromUrl({ search });
      if (search) {
        lowonganStore.setFilters(urlFilters);
      } else {
        jobFilters.clearSearch();
      }
      lowonganStore.fetchLatestJobs(1);
    }
  }
);

watch([loading, latestJobs], ([isLoading, jobs]) => {
  if (!isLoading && lastSearchParams && jobs.length >= 0) {
    const hasFilters =
      lastSearchParams.search ||
      lastSearchParams.location ||
      lastSearchParams.education ||
      lastSearchParams.experience;
    if (hasFilters) {
      showToast(t('home.jobsFoundToast', { total: lowonganStore.totalJobs }), 'success');
      lastSearchParams = null;
    }
  }
});

const currentPage = computed(() => lowonganStore.currentPage);
watch(currentPage, (newPage) => {
  if (newPage > 1) {
    gtagEvent('load_more_jobs', {
      page_number: newPage,
      total_jobs_displayed: latestJobs.value.length,
    });
  }
});

const handleSliderNav = (direction) => {
  autoScroll.scrollSlider(direction);
  gtagEvent('scroll_recommended_jobs', {
    interaction_type: 'button_click',
    direction: direction,
  });
};

onMounted(async () => {
  initFlowbite();

  let initialQuery = { ...route.query };
  if (route.params.kota && !initialQuery.search) {
    initialQuery.search = route.params.kota.replace(/-/g, ' ');
  }

  const urlFilters = jobFilters.initFromUrl(initialQuery);
  const hasFilters = Object.keys(urlFilters).length > 0;

  if (hasFilters) {
    lowonganStore.setFilters(urlFilters);
  }

  // Jalankan secara sequential (bukan Promise.all) agar tidak berebut bandwidth
  // pada jaringan seluler lambat. Recommended jobs diprioritaskan karena
  // posisinya paling atas (above the fold) sehingga pengguna segera melihat konten.
  try {
    await lowonganStore.fetchRecommendedJobs();
  } catch {
    // Error sudah ditangani di store, lanjutkan ke latestJobs
  }

  // Always fetch latest jobs (with or without filters)
  try {
    await lowonganStore.fetchLatestJobs(1);
  } catch {
    // Error sudah ditangani di store, tampilkan komponen ErrorState
  }

  autoScroll.startAutoScroll();
  infiniteScroll.startObserving(() => lowonganStore.loadMore());

  // Track Section Visibility using IntersectionObserver
  const sectionsToObserve = [
    { id: 'recommended-jobs-section', eventName: 'view_recommended_section' },
    { id: 'latest-jobs', eventName: 'view_latest_jobs_section' },
    { id: 'app-footer', eventName: 'view_footer' },
  ];

  const visibilityObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const section = sectionsToObserve.find((s) => s.id === entry.target.id);
          if (section) {
            gtagEvent(section.eventName);
            visibilityObserver.unobserve(entry.target); // Track once per page load
          }
        }
      });
    },
    { threshold: 0.1 }
  );

  sectionsToObserve.forEach((section) => {
    const el = document.getElementById(section.id);
    if (el) visibilityObserver.observe(el);
  });

  // Track Slider Swipe
  const slider = document.getElementById('job-slider');
  if (slider) {
    let userInteracted = false;
    let scrollTracked = false;

    const setInteracted = () => {
      userInteracted = true;
    };

    slider.addEventListener('touchstart', setInteracted, { passive: true });
    slider.addEventListener('mousedown', setInteracted);

    slider.addEventListener(
      'scroll',
      () => {
        if (userInteracted && !scrollTracked) {
          gtagEvent('scroll_recommended_jobs', {
            interaction_type: 'swipe',
          });
          scrollTracked = true;
        }
      },
      { passive: true }
    );
  }
});
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
@keyframes slide-in {
  from {
    transform: translateY(120%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
.animate-slide-in {
  animation: slide-in 0.3s ease-out forwards;
}
/* Shimmer global styles have been moved to individual components but keeping this here for other shimmers in HomeView */
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
.shimmer {
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}
.dark .shimmer {
  background: linear-gradient(90deg, #4b5563 25%, #6b7280 50%, #4b5563 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}
</style>
